(function () {
  function setDeleteButtonState(button, isLoading) {
    if (!button) {
      return;
    }

    if (isLoading) {
      button.dataset.deleting = "true";
      button.disabled = true;
      button.setAttribute("aria-busy", "true");
      button.setAttribute("aria-disabled", "true");
      return;
    }

    button.dataset.deleting = "false";
    button.disabled = false;
    button.removeAttribute("aria-busy");
    button.removeAttribute("aria-disabled");
  }

  async function getShopifyCartId() {
    const cookieMatch = `; ${document.cookie}`.match(/;\s*cart=([^;]+)/);
    if (cookieMatch) {
      return cookieMatch[1].slice(-10);
    }

    const response = await fetch("/cart.js", {
      headers: {
        "X-Requested-With": "XMLHttpRequest",
      },
    });

    if (!response.ok) {
      throw new Error("Could not load the current cart");
    }

    const cart = await response.json();
    return cart.token ? cart.token.slice(-10) : null;
  }

  async function loadCart() {
    const response = await fetch("/cart.js", {
      credentials: "same-origin",
      headers: {
        Accept: "application/json",
        "X-Requested-With": "XMLHttpRequest",
      },
    });

    if (!response.ok) {
      throw new Error("Could not load the current cart");
    }

    return response.json();
  }

  async function parseJsonResponse(response) {
    const text = await response.text();

    if (!text) {
      return {};
    }

    try {
      return JSON.parse(text);
    } catch (_error) {
      return { error: text };
    }
  }

  function cartLinesForButton(cart, button) {
    const draftOrderItemId = button.dataset.draftOrderItemId;
    const productableType = button.dataset.productableType;
    const productableId = button.dataset.productableId;

    return (cart.items || []).filter((item) => {
      if (draftOrderItemId) {
        return (
          String(item.properties?._DraftOrderItemID) ===
          String(draftOrderItemId)
        );
      }

      const isProductable =
        String(item.properties?._ProductableType) === String(productableType) &&
        String(item.properties?._ProductableID) === String(productableId);
      const isBackstory =
        String(item.properties?._GiftOptionProductableType) ===
          String(productableType) &&
        String(item.properties?._GiftOptionProductableID) ===
          String(productableId);

      return isProductable || isBackstory;
    });
  }

  function assertExpectedProductable(groupLines, button) {
    const expectedType = button.dataset.productableType;
    const expectedId = button.dataset.productableId;
    if (!expectedType || !expectedId || groupLines.length === 0) return;

    const found = groupLines.some(
      (item) =>
        String(item.properties?._ProductableType) === String(expectedType) &&
        String(item.properties?._ProductableID) === String(expectedId),
    );

    if (!found) {
      throw new Error(
        "The cart changed before this item could be deleted. Please refresh and try again.",
      );
    }
  }

  async function removeCartLineByCurrentKey(lineItemKey) {
    const response = await fetch("/cart/change.js", {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-Requested-With": "XMLHttpRequest",
      },
      body: JSON.stringify({ id: lineItemKey, quantity: 0 }),
    });

    const payload = await parseJsonResponse(response);

    if (!response.ok) {
      throw new Error(
        payload.description ||
          payload.error ||
          "Could not remove this item from your cart",
      );
    }

    return payload;
  }

  async function removeCartLines(button) {
    const removedKeys = [];

    for (let attempt = 1; attempt <= 10; attempt += 1) {
      const cart = await loadCart();
      const groupLines = cartLinesForButton(cart, button);

      if (groupLines.length === 0) return removedKeys;

      if (removedKeys.length === 0) {
        assertExpectedProductable(groupLines, button);
      }

      // Resolve the key from the latest cart before every mutation. Shopify
      // can change line keys when properties or discounts change.
      const currentLine = groupLines[0];
      await removeCartLineByCurrentKey(currentLine.key);
      removedKeys.push(currentLine.key);
    }

    throw new Error("Could not verify that every linked cart line was removed");
  }

  async function softDeleteDraftOrderItem(
    button,
    shopifyCartId,
    removedLineKeys,
  ) {
    let lastError = null;

    for (let attempt = 1; attempt <= 3; attempt += 1) {
      try {
        const response = await fetch(
          `/apps/frame/draft_order_items/${button.dataset.draftOrderItemId}/soft_delete`,
          {
            method: "POST",
            credentials: "same-origin",
            headers: {
              "Content-Type": "application/json",
              "X-Requested-With": "XMLHttpRequest",
            },
            body: JSON.stringify({
              shopify_cart_id: shopifyCartId,
              productable_type: button.dataset.productableType,
              productable_id: button.dataset.productableId,
              removed_shopify_line_item_keys: removedLineKeys,
            }),
          },
        );
        const payload = await parseJsonResponse(response);

        if (response.ok) return payload;

        lastError = new Error(payload.error || "Could not delete this item");
      } catch (error) {
        lastError = error;
      }
    }

    throw lastError || new Error("Could not delete this item");
  }

  async function deleteFramefoxProductable(button) {
    if (!button || button.dataset.deleting === "true") {
      return;
    }

    const draftOrderItemId = button.dataset.draftOrderItemId;
    if (!draftOrderItemId && !button.dataset.productableId) {
      return;
    }

    const pill = button.querySelector(".cart-action-pill");
    const originalLabel = pill ? pill.textContent : null;

    setDeleteButtonState(button, true);

    if (pill) {
      pill.textContent = "Deleting";
    }

    try {
      const shopifyCartId = await getShopifyCartId();

      if (!shopifyCartId) {
        throw new Error("Could not determine the current Shopify cart");
      }

      const removedLineKeys = await removeCartLines(button);
      if (draftOrderItemId) {
        await softDeleteDraftOrderItem(button, shopifyCartId, removedLineKeys);
      }

      window.location.reload();
    } catch (error) {
      console.error("Error deleting Framefox cart item:", error);
      alert(error.message || "Failed to delete item. Please try again.");
    } finally {
      setDeleteButtonState(button, false);

      if (pill && originalLabel) {
        pill.textContent = originalLabel;
      }
    }
  }

  window.deleteFramefoxProductable = deleteFramefoxProductable;
})();
